import { useTranslation } from "react-i18next";
import { SetStateAction, useEffect, useState } from "react";
import {
	LicenseDesignation,
	LicenseType,
	Severity,
	WeightUnit,
} from "node-library/dist/constants";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import { useSnackbar } from "../../../common-components/toast/showToastMessage";
import { FrontendUserLicense } from "node-library/dist/portal";
import {
	CustomDropdown,
	CustomTextInput,
} from "../../../common-components/inputs";
import { ModalWithButtons } from "../../../common-components/modals/modal";
import { BlueButton } from "../../../common-components/button";
import { createUUID } from "node-library/dist/helpers/request_helper";

interface Props {
	open: boolean;
	setOpen: React.Dispatch<React.SetStateAction<boolean>>;
	license?: FrontendUserLicense;
	proceedAction: (license: FrontendUserLicense) => void;
}

export default function AddOrEditLicenseModal(props: Props) {
	const { t: translation } = useTranslation();
	const { openSnackbar } = useSnackbar();

	const { proceedAction, open, setOpen } = props;

	const [licenseDesignation, setLicenseDesignation] =
		useState<LicenseDesignation>(
			props.license?.licenseDesignation || LicenseDesignation.MEDICAL
		);
	const [licenseType, setLicenseType] = useState<LicenseType>(
		props.license?.licenseType || LicenseType.LICENSED_PRODUCER
	);
	const [licenseNumber, setLicenseNumber] = useState<string>(
		props.license?.licenseNumber || ""
	);
	const [nickName, setNickName] = useState<string>(
		props.license?.nickName || ""
	);

	//validation
	const [licenseNumberErrorMsg, setLicenseNumberErrorMsg] =
		useState<string>("");

	useEffect(() => {
		setLicenseDesignation(
			props.license?.licenseDesignation || LicenseDesignation.MEDICAL
		);
		setLicenseType(props.license?.licenseType || LicenseType.LICENSED_PRODUCER);
		setLicenseNumber(props.license?.licenseNumber || "");
		setNickName(props.license?.nickName || "");
		setLicenseNumberErrorMsg("");
	}, [open]);

	const typeSelector: React.ReactNode = (
		<CustomDropdown
			id="license-type-dropdown"
			label={translation("LicenseType")}
			options={Object.values(LicenseType).map((value) => {
				return { value: value, title: translation(value) };
			})}
			value={licenseType}
			disabled={props.license ? true : false}
			setValue={setLicenseType}
		></CustomDropdown>
	);

	const designationSelector: React.ReactNode = (
		<CustomDropdown
			id="designation-dropdown"
			label={translation("Designation")}
			options={Object.values(LicenseDesignation).map((value) => {
				return { value: value, title: translation(value) };
			})}
			value={licenseDesignation}
			disabled={props.license ? true : false}
			setValue={setLicenseDesignation}
		></CustomDropdown>
	);

	const LicenseNumberInput: React.ReactNode = (
		<CustomTextInput
			id="license-number-input"
			label={`${translation("LicenseNumber")}`}
			value={licenseNumber}
			setValue={setLicenseNumber}
			disabled={props.license ? true : false}
			placeholder={""}
			required
			errorMsg={licenseNumberErrorMsg}
			setErrorMsg={setLicenseNumberErrorMsg}
		/>
	);
	const nickNameInput: React.ReactNode = (
		<CustomTextInput
			id="new-sample-weight-input"
			label={`${translation("Nickname")}`}
			value={nickName}
			setValue={setNickName}
			placeholder={""}
		/>
	);

	const licenseContent: React.ReactNode = (
		<div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
			{typeSelector}
			{designationSelector}

			{LicenseNumberInput}
			{nickNameInput}
		</div>
	);

	const liceneModalModal: React.ReactNode = (
		<ModalWithButtons
			headerText={
				props.license
					? translation("EditLicense")
					: translation("AddNewLicense")
			}
			headerDescription={
				props.license
					? translation("EditLicenseDescription")
					: translation("AddNewLicenseDescription")
			}
			open={open}
			setOpen={setOpen}
			buttons={[
				<div
					style={{ display: "flex", flexDirection: "column", width: "100%" }}
				>
					<BlueButton
						key="add-or-update-license-button"
						id="add-or-update-license-button"
						size="flex"
						children={
							props.license ? translation("Update") : translation("Add")
						}
						onClick={() => {
							setLicenseNumberErrorMsg("");
							if (!licenseNumber || licenseNumber.length === 0) {
								setLicenseNumberErrorMsg(translation("MissingInformation"));
								return;
							}
							let newLicense: FrontendUserLicense;
							if (props.license) {
								newLicense = {
									...props.license,
									nickName: nickName,
								};
							} else {
								newLicense = {
									licenseId: createUUID(),
									licenseDesignation: licenseDesignation,
									licenseType,
									licenseNumber,
									nickName,
									inserted: new Date(),
								};
							}
							proceedAction(newLicense);
						}}
					></BlueButton>
				</div>,
			]}
		>
			{licenseContent}
		</ModalWithButtons>
	);
	return liceneModalModal;
}
